// PreTradeCheckModal — Daily check-in: mood, focus, stress, sleep + intention
import React, { useState, useMemo } from 'react';
import { useMind } from '../../../context/MindContext';
import { INTENTION_OPTIONS } from '../../../config/mindConstants';
import { computeReadinessScore, getReadinessLevel } from '../../../utils/mindScoring';
import ReadinessGauge from './ReadinessGauge';
import { FaBrain, FaTimes } from 'react-icons/fa';

const SLIDER_LABELS = {
  mood: { emoji: '\u{1F60A}', label: 'Mood', low: 'Low', high: 'Great' },
  focus: { emoji: '\u{1F3AF}', label: 'Focus', low: 'Scattered', high: 'Laser' },
  stress: { emoji: '\u{1F9D8}', label: 'Calm', low: 'Stressed', high: 'Calm' },
  sleep: { emoji: '\u{1F634}', label: 'Rest', low: 'Tired', high: 'Rested' },
};

export default function PreTradeCheckModal() {
  const { showPreTradeCheck, submitDailyCheck, skipDailyCheck } = useMind();
  const [values, setValues] = useState({ mood: 3, focus: 3, stress: 3, sleep: 3 });
  const [intention, setIntention] = useState(INTENTION_OPTIONS[0]);

  const preview = useMemo(() => {
    const score = computeReadinessScore(values);
    return { score, level: getReadinessLevel(score) };
  }, [values]);

  if (!showPreTradeCheck) return null;

  const handleSubmit = () => {
    submitDailyCheck({ ...values, intention });
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <FaBrain className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Pre-Trade Check-In</h2>
              <p className="text-[11px] text-gray-500">How are you feeling today?</p>
            </div>
          </div>
          <button
            onClick={skipDailyCheck}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            title="Skip check-in"
          >
            <FaTimes />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Sliders */}
          {Object.entries(SLIDER_LABELS).map(([key, meta]) => (
            <div key={key}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-medium text-gray-700">
                  {meta.emoji} {meta.label}
                </span>
                <span className="text-xs font-bold text-gray-900">{values[key]}/5</span>
              </div>
              <input
                type="range"
                min="1"
                max="5"
                step="1"
                value={values[key]}
                onChange={e => setValues({ ...values, [key]: parseInt(e.target.value) })}
                className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-blue-600"
              />
              <div className="flex justify-between mt-0.5">
                <span className="text-[9px] text-gray-400">{meta.low}</span>
                <span className="text-[9px] text-gray-400">{meta.high}</span>
              </div>
            </div>
          ))}

          {/* Intention */}
          <div>
            <label className="text-xs font-medium text-gray-700 block mb-1.5">
              Today's Intention
            </label>
            <select
              value={intention}
              onChange={e => setIntention(e.target.value)}
              className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2.5 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {INTENTION_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          {/* Live readiness preview */}
          <ReadinessGauge
            score={preview.score}
            level={preview.level}
            label="Readiness"
          />

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              onClick={skipDailyCheck}
              className="flex-1 py-2.5 rounded-lg text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              Skip Today
            </button>
            <button
              onClick={handleSubmit}
              className="flex-1 py-2.5 rounded-lg text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              Start Trading
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
